import {
  useIsMobile,
  WSField,
  WSFlexBox,
  WSForm,
  WSInputMask,
  WSInputText,
  WSList,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { IAddress, IMember } from "@wingspanhq/users/dist/lib";
import { US_STATES_OPTIONS } from "../../../constants/user";
import { SELECT_OPTIONS_CANADIAN_PROVINCES } from "../../../shared/constants/user";

export type FormDataAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string | null;
  postalCode: string;
};

export function formDataToProfile(
  country: string,
  data: FormDataAddress
): IAddress {
  return {
    country,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    city: data.city,
    state: data.state || undefined,
    postalCode: data.postalCode
  };
}

export function getProfileDataAddress(
  member?: IMember,
  options?: {
    homeAddress?: boolean;
  }
): Partial<FormDataAddress> {
  const profileAddress = options?.homeAddress
    ? member?.profile.homeAddress
    : member?.profile.address;

  return {
    addressLine1: profileAddress?.addressLine1,
    addressLine2: profileAddress?.addressLine2,
    city: profileAddress?.city,
    state: profileAddress?.state,
    postalCode: profileAddress?.postalCode
  };
}

type Props = {
  country: string;
  name: string;
  label?: string;
  showRequirements?: boolean;
  disabled?: boolean;
};

export const FormPartialAddress: React.FC<Props> = ({
  country,
  label,
  name,
  showRequirements = true,
  disabled
}) => {
  const isMobile = useIsMobile();
  return (
    <WSField as="div" label={label} required>
      <WSList gap="M">
        <WSForm.Field
          label="Street address"
          name={name + ".addressLine1"}
          component={WSInputText}
          componentProps={{
            disabled,
            placeholder: "Street address",
            fsExclude: true,
            required: showRequirements,
            autoComplete: "address-line1"
          }}
        />
        <WSForm.Field
          label={`Street address line 2 ${
            showRequirements ? "(optional)" : ""
          }`}
          name={name + ".addressLine2"}
          component={WSInputText}
          componentProps={{
            disabled,
            placeholder: "Street address line 2",
            fsExclude: showRequirements,
            autoComplete: "address-line2"
          }}
        />

        {country === "US" ? (
          <WSFlexBox
            wrap="nowrap"
            gap="M"
            key={country + "-city-state-postal"}
            direction={isMobile ? "column" : "row"}
          >
            <WSForm.Field
              label="City"
              name={name + ".city"}
              component={WSInputText}
              componentProps={{
                disabled,
                placeholder: "City",
                required: showRequirements,
                autoComplete: "address-level2"
              }}
            />

            <WSForm.Field
              label="State"
              name={name + ".state"}
              component={WSSelect}
              componentProps={{
                disabled,
                placeholder: "State",
                internalSearch: true,
                options: US_STATES_OPTIONS,
                required: showRequirements
              }}
            />

            <WSForm.Field
              label="Zip code"
              name={name + ".postalCode"}
              component={WSInputMask}
              componentProps={{
                disabled,
                placeholder: "Zip code",
                mask: "99999",
                required: showRequirements,
                autoComplete: "postal-code"
              }}
            />
          </WSFlexBox>
        ) : country === "CA" ? (
          <WSFlexBox
            wrap="nowrap"
            gap="M"
            key={country + "-city-state-postal"}
            direction={isMobile ? "column" : "row"}
          >
            <WSForm.Field
              label="City"
              name={name + ".city"}
              component={WSInputText}
              componentProps={{
                disabled,
                placeholder: "City",
                required: showRequirements,
                autoComplete: "address-level2"
              }}
            />

            <WSForm.Field
              label="Province"
              name={name + ".state"}
              component={WSSelect}
              componentProps={{
                disabled,
                placeholder: "Province",
                internalSearch: true,
                options: SELECT_OPTIONS_CANADIAN_PROVINCES,
                required: showRequirements
              }}
            />

            <WSForm.Field
              label="Postal code"
              name={name + ".postalCode"}
              component={WSInputText}
              componentProps={{
                disabled,
                placeholder: "Postal code",
                required: showRequirements,
                autoComplete: "postal-code"
              }}
            />
          </WSFlexBox>
        ) : (
          <WSFlexBox
            wrap="nowrap"
            gap="M"
            key={country + "-city-state-postal"}
            direction={isMobile ? "column" : "row"}
          >
            <WSForm.Field
              label="City"
              name={name + ".city"}
              component={WSInputText}
              componentProps={{
                disabled,
                placeholder: "City / Town",
                required: showRequirements,
                autoComplete: "address-level2"
              }}
            />

            <WSForm.Field
              label="Region/state (optional)"
              name={name + ".state"}
              component={WSInputText}
              componentProps={{
                disabled,
                placeholder: "Region/state (optional)",
                autoComplete: "address-level1"
              }}
            />

            <WSForm.Field
              label="Postal code"
              name={name + ".postalCode"}
              component={WSInputText}
              componentProps={{
                disabled,
                placeholder: "Postal code",
                required: showRequirements,
                autoComplete: "postal-code"
              }}
            />
          </WSFlexBox>
        )}
      </WSList>
    </WSField>
  );
};
